import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface StatCardWithComparisonProps {
  title: string;
  value: number;
  previousValue?: number | null;
  icon?: React.ReactNode;
  format?: "number" | "percentage" | "rating";
  suffix?: string;
}

export function StatCardWithComparison({
  title,
  value,
  previousValue,
  icon,
  format = "number",
  suffix = "",
}: StatCardWithComparisonProps) {
  // Format the display value
  const formatValue = (val: number) => {
    switch (format) {
      case "percentage":
        return `${Math.round(val)}%`;
      case "rating":
        return val.toFixed(1);
      default:
        return val.toLocaleString();
    }
  };

  // Calculate change
  const hasComparison = previousValue !== null && previousValue !== undefined;
  let change = 0;
  let changePercent = 0;
  let isPositive = true;
  let isNeutral = false;

  if (hasComparison) {
    change = value - previousValue;
    changePercent = previousValue > 0 ? Math.round((change / previousValue) * 100) : 0;
    isPositive = change >= 0;
    isNeutral = change === 0;
  }

  return (
    <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm text-muted-foreground truncate">{title}</p>
            <p className="text-3xl font-bold mt-1">
              {formatValue(value)}{suffix}
            </p>
            {hasComparison && (
              <div className="flex items-center gap-1.5 mt-2">
                {isNeutral ? (
                  <Minus className="h-4 w-4 text-muted-foreground" />
                ) : isPositive ? (
                  <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />
                )}
                <span
                  className={`text-sm ${
                    isNeutral
                      ? "text-muted-foreground"
                      : isPositive
                      ? "text-green-600 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {isNeutral
                    ? "No change"
                    : `${isPositive ? "+" : ""}${change.toFixed(format === "rating" ? 1 : 0)}${format === "percentage" ? "pp" : ""} (${changePercent > 0 ? "+" : ""}${changePercent}%)`}
                </span>
              </div>
            )}
          </div>
          {icon && (
            <div className="h-12 w-12 rounded-md bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
              {icon}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
